import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, UsersIcon, MapPinIcon, ZapIcon, SearchIcon, LayoutIcon, CodeIcon, MousePointerClickIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
import { AlkmaarSkyline } from '../components/AlkmaarSkyline';
export function WebsiteLatenMakenAlkmaarPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website laten maken Alkmaar | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website laten maken in Alkmaar? Noord Digital bouwt professionele, conversiegerichte websites voor ondernemers in Alkmaar en omgeving. Vraag een vrijblijvend gesprek aan.';
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Wat kost een website in Alkmaar?',
    a: 'De kosten voor een website in Alkmaar variëren afhankelijk van je wensen en het type website. Een eenvoudige bedrijfswebsite begint vanaf een paar honderd euro, terwijl maatwerk websites meer kosten. Wij geven altijd vooraf een transparante prijsopgave.'
  }, {
    q: 'Hoe lang duurt het om een website te maken?',
    a: 'Een standaard bedrijfswebsite is meestal binnen 2 tot 4 weken live. Voor complexere projecten plannen we samen een realistische tijdlijn. Wij houden je gedurende het hele proces op de hoogte.'
  }, {
    q: 'Kan ik mijn website zelf aanpassen?',
    a: 'Ja, wij bouwen onze websites met WordPress, zodat je zelf eenvoudig teksten en afbeeldingen kunt aanpassen. Na oplevering krijg je een uitgebreide uitleg en handleiding.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2049">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2050">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2051">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2052" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2053" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2054" />
        </div>

        {/* Alkmaar Skyline */}
        <div className="absolute bottom-0 left-0 right-0 opacity-[0.08] pointer-events-none" data-id="element-2055">
          <AlkmaarSkyline className="w-full h-auto" color="#ffffff" data-id="element-2056" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2057">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2058">
            Website laten maken in Alkmaar
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2059">
            Professionele websites voor ondernemers in Alkmaar en omgeving. Wij
            zijn jouw lokale webdesigner met persoonlijke aanpak.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2060">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2061">
              Plan een gesprek
              <ArrowRightIcon className="w-5 h-5" data-id="element-2062" />
            </a>
          </div>
        </div>
      </section>

      {/* Voor ondernemers in Alkmaar */}
      <section className="py-24 bg-surface-muted" data-id="element-2063">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2064">
          <div className="text-center mb-12" data-id="element-2065">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2066">
              Voor ondernemers in Alkmaar
            </h2>
          </div>
          <div className="max-w-3xl mx-auto text-center" data-id="element-2067">
            <p className="text-lg text-slate-600 leading-relaxed" data-id="element-2068">
              Als webdesign bureau in Alkmaar begrijpen wij de lokale markt. Wij
              helpen ondernemers in Alkmaar, Heerhugowaard, Bergen, Heiloo en
              omgeving met professionele websites die klanten opleveren. Of je
              nu een nieuwe website nodig hebt of je huidige site wilt
              verbeteren — wij staan voor je klaar.
            </p>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-2069">
              Of je nu een nieuwe website wilt laten maken, beter gevonden wilt
              worden in Google, of je huidige website wilt vernieuwen en
              verbeteren. Wij staan voor je klaar.
            </p>
          </div>
        </div>
      </section>

      {/* Waarom een lokale webdesigner */}
      <section className="py-24 bg-white" data-id="element-2070">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2071">
          <div className="text-center mb-16" data-id="element-2072">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2073">
              Waarom een lokale webdesigner
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2074">
              Kies voor een webdesign bureau in Alkmaar dat dichtbij is en met
              je meedenkt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2075">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2076">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2077">
                <UsersIcon className="w-6 h-6 text-accent" data-id="element-2078" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2079">
                Persoonlijk contact
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2080">
                Direct contact met je vaste webdesigner. Geen callcenter, maar
                een persoonlijke aanpak.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2081">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2082">
                <MapPinIcon className="w-6 h-6 text-accent" data-id="element-2083" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2084">
                Lokale kennis
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2085">
                Wij kennen de Alkmaarse markt en weten wat werkt voor lokale
                ondernemers.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2086">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2087">
                <ZapIcon className="w-6 h-6 text-accent" data-id="element-2088" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2089">
                Snelle communicatie
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2090">
                Korte lijnen en snelle reactietijden. Binnen 24 uur antwoord op
                je vraag.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2091">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2092">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-2093" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2094">
                Betere SEO
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2095">
                Geoptimaliseerd voor lokale zoekresultaten in Alkmaar en
                Noord-Holland.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat we bouwen */}
      <section className="py-24 bg-surface-muted" data-id="element-2096">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2097">
          <div className="text-center mb-16" data-id="element-2098">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2099">
              Wat we bouwen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2100">
              Van simpele bedrijfswebsites tot uitgebreide WordPress
              oplossingen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2101">
            <div className="bg-white border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2102">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center mb-6" data-id="element-2103">
                <LayoutIcon className="w-6 h-6 text-navy-700" data-id="element-2104" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2105">
                Bedrijfswebsites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2106">
                Een professioneel online visitekaartje dat jouw diensten en
                expertise presenteert aan klanten in Alkmaar.
              </p>
            </div>
            <div className="bg-white border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2107">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center mb-6" data-id="element-2108">
                <CodeIcon className="w-6 h-6 text-navy-700" data-id="element-2109" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2110">
                WordPress websites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2111">
                Flexibele WordPress websites die je zelf eenvoudig kunt beheren
                en aanpassen.
              </p>
            </div>
            <div className="bg-white border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2112">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center mb-6" data-id="element-2113">
                <MousePointerClickIcon className="w-6 h-6 text-navy-700" data-id="element-2114" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2115">
                Conversie websites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2116">
                Websites gebouwd om bezoekers om te zetten in klanten, met
                slimme call-to-actions en formulieren.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ons proces */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-2117">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-2118" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2119">
          <div className="text-center mb-16" data-id="element-2120">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2121">
              Ons proces
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-2122">
              In 5 heldere stappen naar jouw nieuwe website.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-2123">
            {['Kennismaking', 'Strategie', 'Ontwerp', 'Ontwikkeling', 'Livegang'].map((step, i) => <div key={step} className="bg-white/[0.06] border border-white/10 p-6 rounded-2xl text-center relative" data-id="element-2124">
                <span className="absolute top-2 right-4 text-4xl font-bold text-white/[0.05]" data-id="element-2125">
                  {i + 1}
                </span>
                <div className="w-10 h-10 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto mb-4 font-bold" data-id="element-2126">
                  {i + 1}
                </div>
                <h3 className="text-white font-bold" data-id="element-2127">{step}</h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-2128">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2129">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2130">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2131">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2132">
              Website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2133">•</span>
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2134">
              WordPress websites
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2135">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2136">
              Hosting & onderhoud
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-2137">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2138">
          <div className="text-center mb-16" data-id="element-2139">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2140">
              Veelgestelde vragen
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2141">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-2142">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2143">
                  <span className="font-bold text-navy-900" data-id="element-2144">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2145" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2146" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2147">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2148" />
    </main>;
}